import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';

// Types for environment configuration
export interface EnvironmentConfig {
  appSyncUrl: string;
  region: string;
  [key: string]: any;
}

// Keep track of the configured client to avoid redundant creation
let apolloClient: ApolloClient | null = null;
let configuredGraphqlEndpoint: string | null = null;

/**
 * Create Apollo Client with modern v4 patterns and AWS AppSync integration
 */
function createApolloClientInstance(
  endpoint: string,
  getAuthToken: () => Promise<string | undefined>
): ApolloClient {
  // Create HTTP link for GraphQL endpoint
  const httpLink = new HttpLink({
    uri: endpoint,
  });

  // Create auth link that adds JWT token to requests
  const authLink = setContext(async (_, context: any) => {
    const existingHeaders = context.headers || {};

    try {
      const token = await getAuthToken();

      return {
        headers: {
          ...existingHeaders,
          authorization: token ? `Bearer ${token}` : '',
        },
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to get auth token for GraphQL request:', error);
      return {
        headers: {
          ...existingHeaders,
        },
      };
    }
  });

  // Error link for handling GraphQL and network errors
  const errorLink = new ErrorLink(
    ({ graphQLErrors, networkError, operation }) => {
      const { operationName } = operation;

      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Operation: ${operationName}`
          );
        });
      }

      if (networkError) {
        console.error(`[Network error]: ${networkError.message}`, networkError);
      }
    }
  );

  // Combine all links using Apollo v3 pattern
  const link = from([errorLink, authLink, httpLink]);

  // Create Apollo Client with v3 patterns
  return new ApolloClient({
    link,
    cache: new InMemoryCache({
      // Type policies for Apollo Client v3
      typePolicies: {
        Query: {
          fields: {
            search: {
              // Cache search results separately by query parameters
              keyArgs: ['query'],
              merge(existing, incoming) {
                return incoming;
              },
            },
            buyer: {
              // Cache buyers by ID
              keyArgs: ['id'],
            },
            userSearch: {
              // Cache user searches separately
              keyArgs: ['query'],
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
        Buyer: {
          fields: {
            // Handle buyer entity caching
            users: {
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
        User: {
          fields: {
            buyers: {
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
      },
    }),
    // Default options for queries and mutations (Apollo v4 style)
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
      },
      mutate: {
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      },
    },
  });
}

/**
 * Configure Apollo Client with environment and auth
 */
export function configureApolloClient(
  environmentConfig: EnvironmentConfig,
  getAuthToken: () => Promise<string | undefined>
): ApolloClient {
  const endpoint = environmentConfig.appSyncUrl;

  if (!endpoint) {
    throw new Error('AppSync GraphQL endpoint is not configured');
  }

  // Return existing client if endpoint hasn't changed
  if (apolloClient && configuredGraphqlEndpoint === endpoint) {
    return apolloClient;
  }

  apolloClient = createApolloClientInstance(endpoint, getAuthToken);
  configuredGraphqlEndpoint = endpoint;

  return apolloClient;
}

/**
 * Get the configured Apollo Client instance
 */
export function getApolloClient(): ApolloClient | null {
  return apolloClient;
}

/**
 * Reset Apollo Client (useful for environment switching)
 */
export function resetApolloClient(): void {
  apolloClient = null;
  configuredGraphqlEndpoint = null;
}

/**
 * Initialize Apollo Client with auth integration
 */
export async function initializeApolloClient(
  environmentConfig: EnvironmentConfig,
  getAuthToken: () => Promise<string | undefined>
): Promise<ApolloClient> {
  const client = configureApolloClient(environmentConfig, getAuthToken);
  return client;
}
